import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { lienSuiviAPI } from '../lib/lienSuiviApi';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MapPin, Activity, Loader2, Truck, Fuel, Navigation } from 'lucide-react';
import TripTimeline from '../components/animations/TripTimeline';
import { formatDate, formatDateHeure } from '../lib/formats';
import { formatVolume } from '../lib/utils';
import { libelle, nomPays, referenceLisible, texteSur } from '../lib/libelles';

const Ligne = ({ icone: Icone, titre, children, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <Icone className="h-5 w-5 shrink-0 text-info" aria-hidden="true" />
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{titre}</p>
      <div className="font-semibold">{children}</div>
    </div>
  </div>
);

/**
 * Page publique de suivi d'un voyage, ouverte par un lien partagé (sans compte).
 * Elle ne montre que ce que le serveur renvoie par sa liste d'autorisation:
 * le trajet, l'avancement, les volumes et la dernière position. Aucun montant,
 * aucune heure d'arrivée estimée.
 */
export default function SharedTripView() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSharedTrip = async () => {
      try {
        setTrip(await lienSuiviAPI.lirePublic(id, searchParams.get('token')));
      } catch (err) {
        setError('Ce lien n’est plus valable.');
      } finally {
        setLoading(false);
      }
    };
    fetchSharedTrip();
  }, [id, searchParams]);

  if (loading) return <div className="flex justify-center items-center h-screen"><Loader2 className="animate-spin w-8 h-8 text-info"/></div>;
  if (error || !trip) return <div className="p-8 text-center text-destructive font-bold" data-testid="lien-invalide">{error}</div>;

  const position = trip.latest_position;
  const aPosition = position && position.latitude != null && position.longitude != null;
  const volume = trip.delivered_liters || trip.loaded_liters;
  const trajet =
    trip.departure_country || trip.destination_country
      ? `${nomPays(trip.departure_country)} → ${nomPays(trip.destination_country)}`
      : null;

  return (
    <div className="max-w-xl mx-auto p-4 bg-muted/50 min-h-screen" data-testid="suivi-public">
      <div className="text-center mb-6">
        <h1>{texteSur(trip.organization_name, 'Suivi de livraison')}</h1>
        <p className="text-sm text-muted-foreground">Suivi de votre livraison</p>
      </div>

      <Card className="mb-4 border-t-4 border-t-blue-500">
        <CardContent className="p-4 flex flex-col gap-3">
          <div className="flex justify-between items-center border-b pb-2">
            <span className="text-muted-foreground text-sm">Référence</span>
            <span className="font-bold">{referenceLisible(trip.reference, 'Sans référence')}</span>
          </div>
          <Ligne icone={MapPin} titre="Destination" testid="suivi-destination">
            {texteSur(trip.destination, 'Non précisée')}
          </Ligne>
          <Ligne icone={Activity} titre="Statut actuel" testid="suivi-statut">
            <span className="text-success">{libelle('tripStatus', trip.status)}</span>
          </Ligne>
          {trip.tractor_plate ? (
            <Ligne icone={Truck} titre="Camion" testid="suivi-camion">
              {trip.tractor_plate}
              {trip.tank_plate ? <span className="text-muted-foreground">{' · '}{trip.tank_plate}</span> : null}
            </Ligne>
          ) : null}
          {volume ? (
            <Ligne icone={Fuel} titre={trip.delivered_liters ? 'Volume livré' : 'Volume chargé'} testid="suivi-volume">
              {formatVolume(volume)}
            </Ligne>
          ) : null}
          {trajet ? (
            <Ligne icone={Navigation} titre={trip.departure_date ? `Départ le ${formatDate(trip.departure_date)}` : 'Trajet'} testid="suivi-trajet">
              {trajet}
            </Ligne>
          ) : null}
          {aPosition ? (
            <Ligne icone={MapPin} titre="Dernière position" testid="suivi-position">
              <a
                className="text-primary underline-offset-2 hover:underline"
                href={`https://www.openstreetmap.org/?mlat=${position.latitude}&mlon=${position.longitude}#map=12/${position.latitude}/${position.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {Number(position.latitude).toFixed(4)}, {Number(position.longitude).toFixed(4)}
              </a>
              {position.timestamp ? (
                <span className="text-muted-foreground">{' · '}{formatDateHeure(position.timestamp)}</span>
              ) : null}
            </Ligne>
          ) : null}
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader className="py-3 px-4 bg-muted">
          <CardTitle className="text-sm">Avancement du trajet</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <TripTimeline currentStatus={trip.status} />
        </CardContent>
      </Card>

      <div className="text-center mt-8 text-xs text-muted-foreground/70">
        E-Carbu
      </div>
    </div>
  );
}
